/**
 * Mortgage Calculator Hook
 *
 * Handles all mortgage calculation logic including:
 * - Monthly payment breakdown
 * - Amortization schedule generation
 * - Input state management with localStorage persistence
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import type {
    MortgageInputs,
    MortgageBreakdown,
    AmortizationRow,
    AmortizationSummary,
    AmortizationYear,
    OtherExpense,
} from '@/types/mortgage';
import { DEFAULT_MORTGAGE_INPUTS, MAX_OTHER_EXPENSES } from '@/types/mortgage';

const STORAGE_KEY = 'mortgage_calculator_inputs';

function newExpenseId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `exp_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
}

/**
 * Calculate monthly principal and interest payment
 */
function calculateMonthlyPI(
    loanAmount: number,
    annualRate: number,
    termYears: number
): number {
    if (loanAmount <= 0) return 0;

    const monthlyRate = annualRate / 100 / 12;
    const numberOfPayments = termYears * 12;

    if (monthlyRate === 0) {
        return loanAmount / numberOfPayments;
    }

    return (
        loanAmount *
        (monthlyRate / (1 - Math.pow(1 + monthlyRate, -numberOfPayments)))
    );
}

/**
 * Generate full amortization schedule
 */
function generateAmortizationSchedule(
    loanAmount: number,
    annualRate: number,
    termYears: number,
    monthlyTaxes: number,
    monthlyPMI: number
): AmortizationRow[] {
    const rows: AmortizationRow[] = [];
    const monthlyRate = annualRate / 100 / 12;
    const numberOfPayments = termYears * 12;
    const monthlyPI = calculateMonthlyPI(loanAmount, annualRate, termYears);

    let balance = loanAmount;
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() + 1);
    startDate.setDate(1);

    for (let month = 1; month <= numberOfPayments; month++) {
        const interestPayment = balance * monthlyRate;
        let principalPayment = monthlyPI - interestPayment;

        // Adjust last payment
        if (month === numberOfPayments) {
            principalPayment = balance;
            balance = 0;
        } else {
            balance -= principalPayment;
        }

        const currentDate = new Date(startDate);
        currentDate.setMonth(startDate.getMonth() + month - 1);

        rows.push({
            month,
            date: currentDate,
            principal: principalPayment,
            interest: interestPayment,
            taxes: monthlyTaxes,
            pmi: monthlyPMI,
            totalPayment: principalPayment + interestPayment + monthlyTaxes + monthlyPMI,
            balance: Math.max(0, balance),
        });
    }

    return rows;
}

/**
 * Group amortization rows by year
 */
function groupByYear(rows: AmortizationRow[]): AmortizationYear[] {
    const grouped: Record<string, AmortizationRow[]> = {};

    rows.forEach((row) => {
        const year = row.date.getFullYear().toString();
        if (!grouped[year]) {
            grouped[year] = [];
        }
        grouped[year].push(row);
    });

    return Object.entries(grouped)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .map(([year, yearRows]) => ({
            year,
            rows: yearRows,
            yearlyPrincipal: yearRows.reduce((sum, r) => sum + r.principal, 0),
            yearlyInterest: yearRows.reduce((sum, r) => sum + r.interest, 0),
            yearlyTotal: yearRows.reduce((sum, r) => sum + r.totalPayment, 0),
        }));
}

/**
 * Load saved inputs from localStorage, migrating the legacy single-other-expense
 * shape (otherMonthlyExpense / otherExpenseLabel) into the new otherExpenses array.
 */
function loadSavedInputs(): MortgageInputs {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return DEFAULT_MORTGAGE_INPUTS;
        const parsed: Record<string, unknown> = JSON.parse(saved);

        // Legacy migration
        if (!Array.isArray(parsed.otherExpenses)) {
            const legacyAmount = typeof parsed.otherMonthlyExpense === 'number'
                ? parsed.otherMonthlyExpense
                : 0;
            const legacyLabel = typeof parsed.otherExpenseLabel === 'string'
                ? parsed.otherExpenseLabel
                : 'Other Monthly Expense';
            parsed.otherExpenses = legacyAmount > 0
                ? [{ id: newExpenseId(), label: legacyLabel, amount: legacyAmount }]
                : [];
        }
        delete parsed.otherMonthlyExpense;
        delete parsed.otherExpenseLabel;

        return { ...DEFAULT_MORTGAGE_INPUTS, ...(parsed as Partial<MortgageInputs>) };
    } catch {
        return DEFAULT_MORTGAGE_INPUTS;
    }
}

/**
 * Save inputs to localStorage
 */
function saveInputs(inputs: MortgageInputs): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
    } catch {
        // Ignore storage errors
    }
}

export interface UseMortgageCalculatorReturn {
    // State
    inputs: MortgageInputs;
    breakdown: MortgageBreakdown;
    amortization: AmortizationSummary;

    // Actions
    updateInput: <K extends keyof MortgageInputs>(
        key: K,
        value: MortgageInputs[K]
    ) => void;
    updatePurchasePrice: (price: number) => void;
    updateDownPaymentByAmount: (amount: number) => void;
    updateDownPaymentByPercentage: (percentage: number) => void;
    addOtherExpense: (expense?: Partial<Omit<OtherExpense, 'id'>>) => void;
    updateOtherExpense: (id: string, patch: Partial<Omit<OtherExpense, 'id'>>) => void;
    removeOtherExpense: (id: string) => void;
    resetToDefaults: () => void;
    loadInputs: (next: Partial<MortgageInputs>) => void;
    saveToStorage: () => void;
}

/**
 * Main mortgage calculator hook
 */
export function useMortgageCalculator(): UseMortgageCalculatorReturn {
    const [inputs, setInputs] = useState<MortgageInputs>(loadSavedInputs);

    // Calculate loan amount
    const loanAmount = useMemo(() => {
        return Math.max(0, inputs.purchasePrice - inputs.downPayment);
    }, [inputs.purchasePrice, inputs.downPayment]);

    // Calculate monthly breakdown
    const breakdown = useMemo<MortgageBreakdown>(() => {
        const principalAndInterest = calculateMonthlyPI(
            loanAmount,
            inputs.interestRate,
            inputs.loanTerm
        );

        const taxes = inputs.annualPropertyTax / 12;

        // PMI only applies if down payment < 20%
        const mortgageInsurance =
            inputs.downPaymentPercentage < 20
                ? (loanAmount * (inputs.pmiPercentage / 100)) / 12
                : 0;

        // Calculate others (home insurance is yearly, other expenses are monthly)
        const homeInsuranceMonthly = inputs.annualHomeInsurance / 12;
        const otherExpensesTotal = inputs.otherExpenses.reduce(
            (sum, e) => sum + (Number.isFinite(e.amount) ? e.amount : 0),
            0,
        );

        const others =
            homeInsuranceMonthly +
            inputs.monthlyHOA +
            otherExpensesTotal;

        const total = principalAndInterest + taxes + mortgageInsurance + others;

        return {
            principalAndInterest,
            taxes,
            mortgageInsurance,
            others,
            total,
            loanAmount,
        };
    }, [inputs, loanAmount]);

    // Generate amortization schedule
    const amortization = useMemo<AmortizationSummary>(() => {
        const rows = generateAmortizationSchedule(
            loanAmount,
            inputs.interestRate,
            inputs.loanTerm,
            breakdown.taxes,
            breakdown.mortgageInsurance
        );

        const totalPrincipal = rows.reduce((sum, r) => sum + r.principal, 0);
        const totalInterest = rows.reduce((sum, r) => sum + r.interest, 0);
        const totalPayments = rows.reduce((sum, r) => sum + r.totalPayment, 0);

        return {
            totalPrincipal,
            totalInterest,
            totalPayments,
            years: groupByYear(rows),
        };
    }, [loanAmount, inputs.interestRate, inputs.loanTerm, breakdown.taxes, breakdown.mortgageInsurance]);

    // Update single input
    const updateInput = useCallback(
        <K extends keyof MortgageInputs>(key: K, value: MortgageInputs[K]) => {
            setInputs((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    // Update purchase price and recalculate down payment
    const updatePurchasePrice = useCallback((price: number) => {
        setInputs((prev) => {
            const newDownPayment = (price * prev.downPaymentPercentage) / 100;
            return {
                ...prev,
                purchasePrice: price,
                downPayment: newDownPayment,
            };
        });
    }, []);

    // Update down payment by amount
    const updateDownPaymentByAmount = useCallback((amount: number) => {
        setInputs((prev) => {
            const clampedAmount = Math.min(amount, prev.purchasePrice);
            const percentage =
                prev.purchasePrice > 0 ? (clampedAmount / prev.purchasePrice) * 100 : 0;
            return {
                ...prev,
                downPayment: clampedAmount,
                downPaymentPercentage: percentage,
            };
        });
    }, []);

    // Update down payment by percentage
    const updateDownPaymentByPercentage = useCallback((percentage: number) => {
        setInputs((prev) => {
            const clampedPercentage = Math.min(Math.max(0, percentage), 100);
            const amount = (prev.purchasePrice * clampedPercentage) / 100;
            return {
                ...prev,
                downPaymentPercentage: clampedPercentage,
                downPayment: amount,
            };
        });
    }, []);

    const addOtherExpense = useCallback(
        (expense: Partial<Omit<OtherExpense, 'id'>> = {}) => {
            setInputs((prev) => {
                if (prev.otherExpenses.length >= MAX_OTHER_EXPENSES) return prev;
                const next: OtherExpense = {
                    id: newExpenseId(),
                    label: expense.label?.trim() || 'New Expense',
                    amount: typeof expense.amount === 'number' ? expense.amount : 0,
                };
                return { ...prev, otherExpenses: [...prev.otherExpenses, next] };
            });
        },
        [],
    );

    const updateOtherExpense = useCallback(
        (id: string, patch: Partial<Omit<OtherExpense, 'id'>>) => {
            setInputs((prev) => ({
                ...prev,
                otherExpenses: prev.otherExpenses.map((e) =>
                    e.id === id ? { ...e, ...patch } : e,
                ),
            }));
        },
        [],
    );

    const removeOtherExpense = useCallback((id: string) => {
        setInputs((prev) => ({
            ...prev,
            otherExpenses: prev.otherExpenses.filter((e) => e.id !== id),
        }));
    }, []);

    // Reset to defaults
    const resetToDefaults = useCallback(() => {
        setInputs(DEFAULT_MORTGAGE_INPUTS);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    // Bulk replace — used when loading a saved scenario from the server.
    // Defaults are merged in so older snapshots missing newly-added fields
    // (e.g. `otherExpenses` before PR #301) don't read as `undefined`.
    const loadInputs = useCallback((next: Partial<MortgageInputs>) => {
        setInputs({ ...DEFAULT_MORTGAGE_INPUTS, ...next } as MortgageInputs);
    }, []);

    // Save to storage
    const saveToStorage = useCallback(() => {
        saveInputs(inputs);
    }, [inputs]);

    // Auto-save on changes
    useEffect(() => {
        saveInputs(inputs);
    }, [inputs]);

    return {
        inputs,
        breakdown,
        amortization,
        updateInput,
        updatePurchasePrice,
        updateDownPaymentByAmount,
        updateDownPaymentByPercentage,
        addOtherExpense,
        updateOtherExpense,
        removeOtherExpense,
        resetToDefaults,
        loadInputs,
        saveToStorage,
    };
}
